import React, { useEffect, useState } from 'react';
import Localize from 'react-intl-universal';
import { useSearchParams } from 'react-router-dom';

import { get } from 'lodash-es';

//MUI
import DownloadIcon from '@mui/icons-material/Download';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import { styled, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DataGrid } from '@mui/x-data-grid';

import { PAGE_SIZE_OPTIONS, VISIBLE_TO_PUBLIC_FILTER } from '@common/Constants.js';
import { createAttachmentDownloadService } from '@common/Helper.js';
import getLocalesText from '@common/helpers/tables/getLocalesText.js';
import Logger from '@common/Logger.js';
import NoContent from '@components/NoContentComponent/NoContent.jsx';
import TableToolbar from '@components/TableToolbar/TableToolbar.jsx';
import { defaultTableFilters } from '@config/filterOperators.js';
import useDownload from '@hooks/Download/useDownload';
import useFetching from '@hooks/Fetching/Fetching.js';

import { createAttachmentService } from './AttachmentsHelper.js';
import { attachmentsTableColumns as defaultColumns } from '../config/attachmentsTableColumn.js';

const StyledContainer = styled(Container)(({ theme }) => ({
  padding: 0,
  [theme.breakpoints.up('md')]: {
    margin: 0
  }
}));

const Attachments = ({
  guid,
  configColumns = [],
  minHeight = '',
  eventParticipantMessage = ''
}) => {
  const theme = useTheme();
  const [searchParamsURL] = useSearchParams();
  const allParams = Object.fromEntries(searchParamsURL.entries());

  const showAllAttachments =
    eventParticipantMessage === Localize.get('Catalog.ParticipationConfirmed') &&
    allParams?.participant
      ? true
      : false;

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: get(PAGE_SIZE_OPTIONS, 1, 10)
  });
  const [attachmentId, setAttachmentId] = useState();

  const [attachments, isLoadingAttachments, , fetchAttachments] = useFetching(
    createAttachmentService.bind(
      null,
      guid,
      showAllAttachments ? null : [VISIBLE_TO_PUBLIC_FILTER]
    ),
    true
  );

  // config will override or extend default columns
  const columns = defaultColumns.map((el) => ({
    ...el,
    ...(configColumns.find((config) => config?.field === el?.field) || {})
  }));

  const attachmentsArray = attachments?.data?.content || [];

  const [attachmentDownloadData, , , fetchAttachmentDownloadData] = useFetching(
    createAttachmentDownloadService.bind(null, guid, attachmentId, () => {}),
    true
  );
  const { downloadFile } = useDownload(attachmentDownloadData);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (guid) {
          fetchAttachments();
        }
      } catch (e) {
        Logger.error(e);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guid]);

  useEffect(() => {
    downloadFile();
  }, [attachmentDownloadData]);

  const handleDownload = (id) => {
    setAttachmentId(id);
    fetchAttachmentDownloadData();
  };

  const getElement = (params) => {
    switch (params?.field) {
      case 'descriptionField':
        return (
          <Box sx={{ width: '100%' }}>
            <Tooltip title={params?.row?.displayName} arrow>
              <Typography
                noWrap
                variant='body'
                component='div'
                sx={{ margin: '0.5rem 0', fontSize: '1.1rem', fontWeight: 600 }}
              >
                {params?.row?.displayName}
              </Typography>
            </Tooltip>
            <Typography
              noWrap
              variant='body'
              component='div'
              sx={{
                minWidth: '10rem',
                display: 'inline-block',
                opacity: 0.7
              }}
            >
              {`${Localize.get('CourseDetails.type')}: ${params?.row?.extension?.toUpperCase()}`}
            </Typography>
            <Tooltip
              sx={{
                marginLeft: '0.5rem',
                display: 'inline-block'
              }}
              title={`${Localize.get('TrainerAreaAttachmentTable.fileName')}: ${params?.row?.name}`}
              arrow
            >
              <Typography
                noWrap
                variant='body'
                component='div'
                sx={{ opacity: 0.7, display: 'inline-block' }}
              >
                {`${Localize.get('TrainerAreaAttachmentTable.fileName')}: ${params?.row?.name}`}
              </Typography>
            </Tooltip>
          </Box>
        );
      case 'options':
        return (
          <Box>
            <IconButton
              name='download'
              onClick={() => handleDownload(params?.id)}
              aria-label='download'
            >
              <DownloadIcon />
            </IconButton>
          </Box>
        );
      default:
        return <Typography>{params?.value}</Typography>;
    }
  };

  return (
    <>
      <StyledContainer>
        {isLoadingAttachments && (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', width: '100%', minHeight: minHeight }}
          >
            <CircularProgress />
          </Box>
        )}
        {!isLoadingAttachments &&
          (attachmentsArray.length ? (
            <Box sx={{ width: '100%', overflowX: 'auto', minHeight: minHeight }}>
              <DataGrid
                sx={{
                  border: 0,
                  '& .MuiDataGrid-root': {
                    border: 'none'
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold',
                    fontSize: '1rem'
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    minHeight: '4rem',
                    textAlign: 'left'
                  },
                  '& .MuiTablePagination-toolbar p': {
                    marginBottom: '0 !important'
                  },
                  '& .MuiBadge-badge': {
                    marginTop: '2px !important',
                    color: theme.palette.secondary.contrastText
                  },
                  '& .MuiButtonBase-root.MuiButton-root': {
                    color: theme.palette.primary.main
                  },
                  '& .MuiDataGrid-filterFormColumnInput': {
                    color: `${theme.palette.primary.main} !important`
                  },
                  '& .MuiButtonBase-root-MuiMenuItem-root-MuiTablePagination-menuItem.Mui-selected':
                    {
                      background: `${theme.palette.primary.light || theme.palette.primary.main} !important`
                    }
                }}
                rows={attachmentsArray}
                columns={[
                  ...columns.map((column) => ({
                    ...column,
                    headerName: Localize.get(column.headerName),
                    filterOperators: defaultTableFilters,
                    renderCell: (params) => {
                      return (
                        <div
                          data-testid={`${params.field}-${params?.row?.id ?? 'default'}`}
                          style={{ width: '100%' }}
                        >
                          {getElement(params)}
                        </div>
                      );
                    }
                  }))
                ]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                pagination={true}
                slots={{ toolbar: () => <TableToolbar /> }}
                rowHeight={80}
                disableSelectionOnClick
                disableRowSelectionOnClick
                disableColumnMenu
                disableDensitySelector
                slotProps={{
                  filterPanel: {
                    sx: {
                      maxWidth: '85vw',
                      '& .MuiFormLabel-root.MuiInputLabel-root': {
                        color: `${theme.palette.primary.light || theme.palette.primary.main} !important`
                      },
                      '& .MuiInput-root::after': {
                        color: `${theme.palette.primary.light || theme.palette.primary.main} !important`,
                        borderBottom: `${theme.palette.primary.light || theme.palette.primary.main} !important`
                      }
                    }
                  },
                  columnsPanel: {
                    sx: {
                      '& .MuiButtonBase-root.MuiButton-root': {
                        color: theme.palette.primary.light || theme.palette.primary.main
                      },
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: theme.palette.primary.light || theme.palette.primary.main
                      },
                      '& .MuiSwitch-track': {
                        background: `${theme.palette.primary.light || theme.palette.primary.main} !important`
                      },
                      '& .MuiInput-root::after': {
                        color: `${theme.palette.primary.light || theme.palette.primary.main} !important`,
                        borderBottom: `${theme.palette.primary.light || theme.palette.primary.main} !important`
                      },
                      '& .MuiFormLabel-root.MuiInputLabel-root': {
                        color: `${theme.palette.primary.light} !important`
                      }
                    }
                  },
                  pagination: {
                    sx: {
                      '& .MuiInputBase-root.MuiTablePagination-select': { marginTop: '12px' }
                    }
                  }
                }}
                localeText={getLocalesText(Localize)}
              />
            </Box>
          ) : (
            <Box sx={{ minHeight: minHeight }}>
              <NoContent infoType={Localize.get('EventDetails.noAttachments')} />
            </Box>
          ))}
      </StyledContainer>
    </>
  );
};

export default Attachments;
